import { DownOutlined, RightOutlined } from '@ant-design/icons'
import { previewCount } from '@services/SegmentService'
import {
  ColorPicker,
  IconPicker,
} from '@supplement-bacon/react-toolkit/components'
import {
  Divider,
  Flex,
  Form,
  FormInstance,
  Input,
  Skeleton,
  theme,
  Typography,
} from 'antd'
import { debounce } from 'lodash'
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import {
  buildCriteriaPayload,
  getCriteriaConfig,
  isCriteriaActive,
} from '../CriteriaConfig'
import {
  SegmentCriteriaPayload,
  SegmentFormValues,
  SegmentStats,
} from '../types'

const { Text } = Typography

const PREVIEW_DEBOUNCE_MS = 500

type Props = {
  form: FormInstance
  /**
   * Bump whenever the values are set from outside (editing an existing
   * segment): the criteria state and the preview count are recomputed, and the
   * criteria the segment already uses are expanded.
   */
  syncKey?: string | number
  /** Extra fields rendered inside the form, e.g. the SBRM hidden id. */
  children?: ReactNode
}

/**
 * Segment name + criteria builder + live matching-contacts preview.
 *
 * Shared by the create and update actions so both edit the exact same criteria
 * tree; the calling action owns prefilling and submission.
 */
const SegmentFields = ({ form, syncKey, children }: Props) => {
  const { token } = theme.useToken()

  const [stats, setStats] = useState<SegmentStats>({
    matchingContacts: 0,
    totalContacts: 0,
  })
  // An update mounts with values on their way in (`syncKey`), so the count is
  // pending from the first paint rather than flashing `0 / 0` first.
  const [isPreviewLoading, setIsPreviewLoading] = useState(
    syncKey !== undefined
  )
  const [activeCriteria, setActiveCriteria] = useState<string[]>([])
  const [expandedItems, setExpandedItems] = useState<string[]>([])

  // Criteria configuration
  const criteriaConfig = useMemo(() => getCriteriaConfig(), [])

  // The store also holds the identity fields (name, description, icon, color,
  // SBRM id), and only the criteria may drive the accordion.
  const criteriaFieldNames = useMemo<string[]>(
    () =>
      criteriaConfig.flatMap((category) =>
        category.items.map((item) => item.fieldName)
      ),
    [criteriaConfig]
  )

  // Ignore stale responses (a newer request has been fired) and responses
  // arriving after unmount.
  const requestIdRef = useRef(0)

  /**
   * Queries the API for the number of matching contacts.
   * Debounced (~500ms) so we don't fire a request on every keystroke.
   *
   * The generation is only captured here, never bumped: `requestPreview` owns
   * the bump so a response from a previous edit cannot clear the loader of a
   * request that is still waiting out its debounce.
   */
  const fetchPreviewCount = useMemo(
    () =>
      debounce(async (criteria: SegmentCriteriaPayload) => {
        const requestId = requestIdRef.current

        try {
          const { matching, total } = await previewCount(criteria)
          if (requestId !== requestIdRef.current) return
          setStats({
            // `matching`/`total` can be null when the backend returns a partial
            // result (MySQL timeout): fall back to 0.
            matchingContacts: matching ?? 0,
            totalContacts: total ?? 0,
            lastUpdated: new Date(),
          })
          setIsPreviewLoading(false)
        } catch {
          if (requestId !== requestIdRef.current) return
          // The API did not answer: don't display a misleading count.
          setIsPreviewLoading(false)
        }
      }, PREVIEW_DEBOUNCE_MS),
    []
  )

  /**
   * Reads the WHOLE store, collapsed criteria included.
   *
   * `getFieldsValue(true)` is the only call shape returning the store itself
   * (`@rc-component/form/es/hooks/useForm.js:208-226`); every other shape
   * rebuilds the values from the mounted fields only, which drops any
   * collapsed criterion. The store comes back by reference, hence the copy:
   * shallow is enough, as rc-form replaces it on every write
   * (`useForm.js:625`) instead of mutating it.
   */
  const readValues = () =>
    ({ ...form.getFieldsValue(true) }) as SegmentFormValues

  const activeCriteriaOf = (values: SegmentFormValues) =>
    criteriaFieldNames.filter((key) => isCriteriaActive(key, values))

  const toggleItem = (fieldName: string) => {
    setExpandedItems((prev) =>
      prev.includes(fieldName)
        ? prev.filter((item) => item !== fieldName)
        : [...prev, fieldName]
    )
  }

  /**
   * Marks the displayed count stale and schedules its recomputation.
   *
   * Synchronous on purpose: the skeleton must show up on edit, not 500ms
   * later, and the reset to `0 / 0` must be immediate.
   */
  const requestPreview = (values: SegmentFormValues) => {
    const criteria = buildCriteriaPayload(values)

    requestIdRef.current++

    // No active criterion: the backend rejects an empty tree (422). Skip the
    // call and reset the count.
    if (criteria.children.length === 0) {
      fetchPreviewCount.cancel()
      setStats({ matchingContacts: 0, totalContacts: 0 })
      setIsPreviewLoading(false)
      return
    }

    setIsPreviewLoading(true)
    fetchPreviewCount(criteria)
  }

  /**
   * Updates the active criteria and triggers the count recomputation.
   *
   * Only a criterion is acted upon: renaming a segment or picking its icon
   * leaves the criteria tree byte-identical, so recomputing would blank the
   * count to a skeleton on every keystroke for the very same result.
   *
   * The `allValues` argument is ignored: it is built from the mounted fields
   * only (`useForm.js:642`), so a collapsed criterion would vanish from the
   * count. The store is already written when the callback fires
   * (`useForm.js:622-626`), so re-reading it needs no merge of the change.
   */
  const handleValuesChange = (changedValues: Partial<SegmentFormValues>) => {
    const touchesCriteria = Object.keys(changedValues ?? {}).some((key) =>
      criteriaFieldNames.includes(key)
    )

    if (!touchesCriteria) return

    const values = readValues()

    setActiveCriteria(activeCriteriaOf(values))
    requestPreview(values)
  }

  // Initialize (and re-initialize on an external prefill) the stats, then clean
  // up the debounce on unmount.
  useEffect(() => {
    const values = readValues()
    const active = activeCriteriaOf(values)

    setActiveCriteria(active)
    // Open the criteria the segment already targets, otherwise an edit shows a
    // set of collapsed headers with no hint of what is configured.
    setExpandedItems((prev) => [...new Set([...prev, ...active])])
    requestPreview(values)

    return () => {
      requestIdRef.current++
      fetchPreviewCount.cancel()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncKey])

  return (
    <Form form={form} layout="vertical" onValuesChange={handleValuesChange}>
      {children}

      {/* Segment name */}
      <Form.Item
        name="name"
        label="Segment name"
        rules={[{ required: true, message: 'The segment name is required.' }]}
      >
        <Input placeholder="Best client" showCount maxLength={100} />
      </Form.Item>

      {/* Description (required) */}
      <Form.Item
        name="description"
        label="Description"
        rules={[{ required: true, message: 'The description is required.' }]}
      >
        <Input.TextArea
          placeholder="Describe this segment"
          autoSize={{ minRows: 2, maxRows: 4 }}
          showCount
          maxLength={255}
        />
      </Form.Item>

      {/* Icon + color (required), stacked, above the segmentation parameters */}
      <Flex vertical>
        <Form.Item
          name="icon"
          label="Icon"
          rules={[{ required: true, message: 'The icon is required.' }]}
        >
          <IconPicker />
        </Form.Item>
        <Form.Item
          name="color"
          label="Color"
          rules={[{ required: true, message: 'The color is required.' }]}
        >
          <ColorPicker />
        </Form.Item>
      </Flex>

      {/* Segmentation criteria */}
      <Flex vertical gap={24}>
        {criteriaConfig.map((category) => (
          <Flex vertical gap={8} key={category.key}>
            <Text>{category.categoryTitle}</Text>

            <Flex vertical gap={8}>
              {category.items.map((item) => {
                const isExpanded = expandedItems.includes(item.fieldName)
                const isActive = activeCriteria.includes(item.fieldName)

                return (
                  <Flex
                    vertical
                    key={item.fieldName}
                    style={{
                      borderRadius: 8,
                      overflow: 'hidden',
                      borderWidth: isActive ? 1 : 0,
                      borderStyle: 'solid',
                      borderColor: token.colorPrimary,
                      background: token.colorBgLayout,
                    }}
                  >
                    {/* Clickable header */}
                    <Flex
                      align="center"
                      justify="space-between"
                      style={{
                        padding: '12px 16px',

                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                      onClick={() => toggleItem(item.fieldName)}
                      className="segment-item-header"
                    >
                      <Flex align="center" gap={12}>
                        <Text strong>{item.title}</Text>
                      </Flex>
                      {isExpanded ? (
                        <DownOutlined style={{ color: '#8c8c8c' }} />
                      ) : (
                        <RightOutlined style={{ color: '#8c8c8c' }} />
                      )}
                    </Flex>

                    {/* Expandable content */}
                    {isExpanded && (
                      <Flex
                        vertical
                        style={{
                          padding: 20,
                          borderTop: `1px solid ${token.colorBgContainerDisabled}`,
                        }}
                      >
                        <Text style={{ marginBottom: 8 }}>
                          {item.description}
                        </Text>
                        {item.component}
                      </Flex>
                    )}
                  </Flex>
                )
              })}
            </Flex>
          </Flex>
        ))}
      </Flex>

      <Divider style={{ margin: '16px 0' }} />

      {/* Dynamic summary */}
      <Flex align="center" justify="center" gap={8}>
        {/*
          The numbers keep their slot while a request is in flight: they stay
          rendered, hidden, and the skeleton is laid over them, so the label
          never shifts between the pending and the idle state. `Skeleton.Node`
          puts `style` on its inner node (`antd/es/skeleton/Node.js`), hence
          the wrapper that gives it the slot to fill.
        */}
        <span
          style={{
            position: 'relative',
            display: 'inline-flex',
            justifyContent: 'center',
            minWidth: 72,
          }}
        >
          <Text
            strong
            style={{ visibility: isPreviewLoading ? 'hidden' : 'visible' }}
          >
            {stats.matchingContacts.toLocaleString('en-US')}
            {' / '}
            {stats.totalContacts.toLocaleString('en-US')}
          </Text>
          {isPreviewLoading && (
            <span style={{ position: 'absolute', inset: 0, display: 'flex' }}>
              <Skeleton.Node
                active
                styles={{ root: { display: 'block', width: '100%' } }}
                style={{ width: '100%', height: '100%' }}
              />
            </span>
          )}
        </span>
        <Text strong>matching contacts</Text>
      </Flex>
    </Form>
  )
}

export default SegmentFields
